import {IconButton} from '@mui/material';
import {useCan} from 'src/permissions/hooks';
import {FC, useState} from 'react';
import {useIntl} from 'react-intl';
import {employeeApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {EditIcon} from 'src/components/icons/EditIcon';
import {EmployeeForm} from 'src/components/forms/EmployeeForm';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch} from 'src/types';
import {EmployeeDTO, UpdateEmployeeRequest} from 'src/types/employee';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {employeeActionsSx} from '../EmployeeActions/EmployeeActions.sx';

type UpdateEmployeeMutationTrigger = ReturnType<
    typeof employeeApi.useUpdateEmployeeByIdMutation
>[0];

const handleUpdateEmployee =
    (
        trigger: UpdateEmployeeMutationTrigger,
        employeeId: number,
        data: UpdateEmployeeRequest,
        dispatch: AppDispatch,
        handleClose: () => void,
        successMessage: string,
        errorMessage: string
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));

                await trigger({employeeId, data})
                    .unwrap()
                    .finally(() => {
                        dispatch(preloaderChangeStatus(false));
                    });

                NotifySuccess(successMessage);
            } catch (error: any) {
                error.status === 400 && NotifyError(errorMessage);
            } finally {
                dispatch(preloaderChangeStatus(false));
                handleClose();
            }
        };

export const EditEmployee: FC<{ row: EmployeeDTO }> = ({row}) => {
    // The control is the action: without the permission the server refuses it, so showing it
    // offers a button whose only outcome is an error.
    const canDoIt = useCan('employees.update');
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();

    const dispatch = useAppDispatch();

    const [triggerUpdateEmployee] = employeeApi.useUpdateEmployeeByIdMutation();

    const intl = useIntl();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'editEmployee'});

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    const handleSubmit = (values: Omit<UpdateEmployeeRequest, 'businessId' | 'companyId'>) => {
        const data: UpdateEmployeeRequest = {
            ...values,
            businessId: businessId!,
            companyId: +row?.company?.id,
        };

        handleUpdateEmployee(
            triggerUpdateEmployee,
            +row?.id,
            data,
            dispatch,
            handleCloseModal,
            intl.formatMessage({id: 'employeeUpdatedSuccessfully'}),
            intl.formatMessage({id: 'cannotUpdateEmployee'})
        )();
    };

    if (!canDoIt) {
        return null;
    }

    return (
        <>
            <IconButton
                sx={employeeActionsSx.button}
                onClick={handleOpenModal}
            >
                <EditIcon/>
            </IconButton>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <EmployeeForm
                    handleClose={handleCloseModal}
                    isEditMode
                    initialValues={{
                        firstName: row.firstName,
                        lastName: row.lastName,
                        title: row.title,
                        consumptionPaymentMode: row.consumptionPaymentMode,
                        language: row.user?.language,
                    }}
                    onSubmit={handleSubmit}
                    onCancel={handleCloseModal}
                />
            </AMPModal>
        </>
    );
};